import { FC, MouseEvent, useMemo, useState } from "react";
import { Avatar, Box, Card, CardContent, Stack } from "@mui/material";
import ReportModal from "@/components/UI/modal/ReportModal/ReportModal";
import DeleteModal from "@/components/UI/modal/DeleteModal/DeleteModal";
import { useAppSelector } from "@/hooks/useAppSelector";
import PostHeader from "./PostHeader";
import PostContent from "./PostContent";
import PostActions from "./PostActions";
import PostFooter from "./PostFooter";
import { styles } from "../stylesHome";
import { IPost } from "@/store/api/postApiData";
import PostContentLong from "./PostContentLong";
import { useLocation, useNavigate } from "react-router-dom";
import { useDeletePostMutation } from "@/store/api/postApi";
import Popup from "@/components/UI/popup/Popup";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";

interface IPostProps {
  post: IPost;
  onDelete?: (uid: string) => void;
  isEditing?: boolean;
}

const Post: FC<IPostProps> = ({ post, onDelete, isEditing }) => {
  const { user } = useAppSelector((state) => state.auth);
  const updatedPost = useAppSelector((state) => state.updatedPosts.posts[post.uid]);
  const [deletePost] = useDeletePostMutation();
  const [zapsCountState, setZapsCountState] = useState(post.zapsCount);
  const [totalZapsAmountState, setTotalZapsAmountState] = useState(post.totalZapsAmount);
  const [openReportModal, setOpenReportModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [isImageValid, setIsImageValid] = useState(true);
  const [reportPostUid, setReportPostUid] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const isSavedPostPage = location.pathname === "/saved-post";
  const [isLikedTemporary, setIsLikedTemporary] = useState(false);
  const handleCloseDeleteModal = () => setOpenDeleteModal(false);
  const handleCloseReportModal = () => setOpenReportModal(false);
  const [avatarsState, setAvatarsState] = useState<string[]>([
    ...new Set(post.reactionsPreview ?? []),
  ]);
  const postForPreview = useMemo(
    () => ({
      ...post,
      zapsCount: zapsCountState,
      totalZapsAmount: totalZapsAmountState,
      likesCount: post.likesCount + (isLikedTemporary && !post.isLiked ? 1 : 0),
      reactionsPreview: avatarsState,
    }),
    [post, zapsCountState, totalZapsAmountState, isLikedTemporary, avatarsState]
  );
  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleReport = (uid: string) => {
    setReportPostUid(uid);
    setOpenReportModal(true);
    handleClose();
  };

  const handleDelete = () => {
    setOpenDeleteModal(true);
    handleClose();
  };

  const handleConfirmDelete = async () => {
    if (onDelete) {
      onDelete(post.uid);
    }

    setOpenDeleteModal(false);

    try {
      await deletePost(post.uid).unwrap();
    } catch (error) {
      console.error("Delete failed", error);
    }
  };
  const navigate = useNavigate();
  const author = post?.author ?? null;
  const authorUid = author?.uid ?? null;
  const authorName = author?.name ?? "";
  const authorAvatarUrl = author?.image?.url ?? "";
  const goToProfile = () => {
    if (authorUid) navigate(`/profile/${authorUid}`);
  };

  return (
    <>
      <Box id={`post-${post.uid}`} sx={{ position: "relative" }}>
        {isEditing && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CustomLoader size={34} />
          </Box>
        )}
      </Box>
      <Card id={`post-${post.uid}`} sx={styles.cardWrapper}>
        {/* Modal */}
        <ReportModal
          handleClose={handleCloseReportModal}
          open={openReportModal}
          postUid={reportPostUid}
          variant="post"
        />

        <DeleteModal
          open={openDeleteModal}
          handleClose={handleCloseDeleteModal}
          onDelete={handleConfirmDelete}
          variant="post"
          title="Are you sure you want to delete the post?"
          description="You won't be able to recover it"
        />

        <Stack position="relative">
          <Popup type="user" uid={authorUid || undefined}>
            <Stack spacing={1}>
              <Avatar
                // @ts-ignore
                src={authorAvatarUrl}
                alt="Profile"
                sx={{
                  width: 48,
                  height: 48,
                  marginRight: "1rem",
                }}
                onClick={goToProfile}
              >
                {(authorName?.[0] ?? "D").toUpperCase()}
              </Avatar>
            </Stack>
          </Popup>
        </Stack>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            maxWidth: "540px",
            width: "100%",
            minWidth: 0,
          }}
        >
          <PostHeader
            {...{
              post,
              user,
              anchorEl,
              open,
              handleClick,
              handleClose,
              handleDelete,
              handleReport,
            }}
          />

          <CardContent sx={styles.cardContent(post.isLongForm)}>
            {post.isLongForm ? (
              <PostContentLong
                post={postForPreview}
                isImageValid={isImageValid}
                setIsImageValid={setIsImageValid}
              />
            ) : (
              <PostContent
                post={post}
                isImageValid={isImageValid}
                setIsImageValid={setIsImageValid}
              />
            )}
          </CardContent>
          <PostFooter
            lastThreeLike={avatarsState}
            post={post}
            zapsCountState={zapsCountState}
            isLikedTemporary={isLikedTemporary}
            totalZapsAmountState={totalZapsAmountState}
            totalLikesAmount={updatedPost?.totalLikesAmount ?? post.totalLikesAmount}
          />
          <PostActions
            post={post}
            onDelete={isSavedPostPage ? onDelete : undefined}
            zapsCountState={zapsCountState}
            setZapsCountState={setZapsCountState}
            isLikedTemporary={isLikedTemporary}
            setIsLikedTemporary={setIsLikedTemporary}
            totalZapsAmountState={totalZapsAmountState}
            setTotalZapsAmountState={setTotalZapsAmountState}
            onLikeSuccess={() => {
              const myAvatar = user?.image?.url ?? "";
              setAvatarsState((prev) => {
                const filtered = prev.filter((a) => a !== myAvatar);
                return [myAvatar, ...filtered].slice(0, 3);
              });
            }}
            onZapSuccess={(avatarUrl) => {
              setAvatarsState((prev) => {
                const filtered = prev.filter((a) => a !== avatarUrl);
                return [avatarUrl, ...filtered].slice(0, 3);
              });
            }}
          />
        </Box>
      </Card>
    </>
  );
};

export default Post;
